import React, { useEffect, useState } from 'react';
import { Loader2, GitCompare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`);
const Stat = ({ label, value, testId }) => (
  <div className="bg-white border border-[#E2E2DF] rounded-md px-4 py-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-2xl font-extrabold text-slate-900 font-mono mt-1">{value}</div>
  </div>
);

export default function VariantInsightsPage() {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/reports/variant-insights').then((r) => setD(r.data)).catch(() => setD({ by_category: [], recent: [] })); }, [api]);
  if (!d) return <div className="p-8 text-slate-500 flex gap-2 items-center" data-testid="variant-insights-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-5" data-testid="variant-insights-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope] flex items-center gap-2"><GitCompare size={26} /> Variant Insights</h1>
        <p className="text-sm text-slate-500 mt-1">Which option clients choose when offered alternatives — logged each time a quote decision is saved.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat testId="vi-groups" label="Option sets decided" value={d.total_groups || 0} />
        <Stat testId="vi-accept-options" label="Accepted · with options" value={pct(d.acceptance_rate_with_options)} />
        <Stat testId="vi-accept-single" label="Accepted · single option" value={pct(d.acceptance_rate_single_option)} />
        <Stat testId="vi-first-wins" label="First variant wins" value={pct(d.first_variant_win_rate)} />
        <Stat testId="vi-cheapest-wins" label="Cheapest option wins" value={pct(d.cheapest_win_rate)} />
      </div>
      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs" data-testid="vi-by-category">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 text-left">
            <tr><th className="px-4 py-2">Quote type</th><th>Sets</th><th>Accepted</th><th>First wins</th><th>Alternative wins</th><th>Alt cheaper / dearer</th><th>Avg uplift vs cheapest</th><th>Avg spread</th></tr>
          </thead>
          <tbody>
            {d.by_category.map((c) => (
              <tr key={c.category} className="border-t border-[#F1EFEC]">
                <td className="px-4 py-2 font-semibold text-slate-800">{c.category}</td><td>{c.groups}</td><td>{pct(c.acceptance_rate)}</td>
                <td>{c.first_wins} ({pct(c.first_win_rate)})</td><td>{c.alt_wins}</td><td>{c.cheaper_alt} / {c.dearer_alt}</td>
                <td className="font-mono">{money(c.avg_uplift_vs_cheapest)}</td><td className="font-mono">{money(c.avg_spread)}</td>
              </tr>
            ))}
            {!d.by_category.length && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">No decided option sets yet — data builds up as variant quotes are accepted or declined.</td></tr>}
          </tbody>
        </table>
      </div>
      {d.recent.length > 0 && (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4" data-testid="vi-recent">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Recent decisions</div>
          {d.recent.map((r) => (
            <div key={r.parent_id + r.group_id} className="text-[11px] py-1 border-t border-[#F7F5F2]">
              <b>{r.category}</b> · {r.outcome === 'accepted' ? `won: ${r.accepted_label} (${r.accepted_is_first ? 'first variant' : `alternative #${r.accepted_position}`}, ${money(r.diff_vs_first)} vs first)` : 'declined'}
              <span className="text-slate-400"> · {r.options.map((o) => `${o.label} ${money(o.value)}`).join(' / ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
